import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { useCurrentUser } from 'entities/user/model';
import { useBill, useBillMutations } from 'entities/bill/model';
import { useVendors } from 'features/quickBooksOnline/useVendors';
import { useAccounts } from 'features/quickBooksOnline/useAccounts';
import { useTaxRates } from 'features/quickBooksOnline/useTaxRates';
import { Spinner } from 'shared/ui/Spinner';
import { useLocation } from 'react-router-dom';
import BillReconcileForm, {
  createBillReference,
} from 'widgets/Bills/BillReconcileForm';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import BigNumber from 'bignumber.js';
import { useCustomers } from 'features/quickBooksOnline/useCustomers';
import { useItems } from 'features/quickBooksOnline/useItems';

const XERO_ZERO_TAX_IDS = ['NONE'];

export const createBillRecociliationPayload = (
  bill,
  companyAccountingData,
  reconcileInput
) => {
  const { baseCurrency, supportedCurrencies } = companyAccountingData;
  const {
    lineItemTaxAmount,
    lineItemTaxId,
    lineItemTaxName,
    newSupplierName,
    newSupplierEmail,
    newSupplier,
    supplierId,
    supplierName,
    nominalAccountId,
    nominalAccountName,
  } = reconcileInput;

  const determineCurrency = () => {
    if (
      bill.token === baseCurrency ||
      supportedCurrencies.some((currency) => currency === bill.token)
    ) {
      return bill.token;
    }

    return 'USD';
  };

  return {
    lineItemTaxAmount,
    lineItemTaxId,
    lineItemTaxName,
    newSupplierName,
    newSupplierEmail,
    newSupplier,
    supplierId,
    supplierName,
    nominalAccountId,
    nominalAccountName,

    // Get from bill or other source

    id: bill.uuid,
    reference: createBillReference(bill),
    baseCurrency: baseCurrency,
    issueDate: bill.billDate,
    dueDate: bill.dueDate,
    lineItemDescription: bill.services,
    lineItemUnitAmount: bill.subtotal,
    lineItemQuantity: 1,
    lineItemSubTotal: Number(bill.subtotal),
    lineItemTotal:
      bill.token === 'ETH' ? Number(bill.dollarAmount) : Number(bill.amount),
    withholdingTaxDescription: 'Withholding Tax',
    withholdingTaxRate: 0,
    status: 'Draft',
    currency: determineCurrency(),
    subTotal: Number(bill.subtotal),
    taxAmount:
      bill.token === 'ETH'
        ? new BigNumber(bill.dollarAmount)
            .minus(new BigNumber(bill.subtotal))
            .toNumber()
        : new BigNumber(bill.amount)
            .minus(new BigNumber(bill.subtotal))
            .toNumber(),
    totalAmount:
      bill.token === 'ETH' ? Number(bill.dollarAmount) : Number(bill.amount),
  };
};

const _reconcileBill = async (getToken, userDetails, billDetails) => {
  const token = await getToken();

  await axios.post(
    process.env.REACT_APP_SERVER_URL + '/codat/create-bill',
    {
      billDetails,
      userDetails,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
};

export const useReconcileBill = (billId) => {
  const { data } = useCurrentUser();
  const { getAccessTokenSilently } = useAuth0();
  const { invalidate: invalidateBills } = useBillMutations();

  const mutation = useMutation({
    mutationKey: ['reconcile-bill', billId],
    mutationFn: (payload) => {
      return _reconcileBill(getAccessTokenSilently, data, payload)
        .then(() => {
          toast.success('Successfully Reconciled Bill', {
            autoClose: 3000,
          });
          invalidateBills();
        })
        .catch(() => {
          toast.error(
            'Was not able to reconcile a Bill, please contact support: pavel@getfractal.xyz',
            {
              autoClose: 6000,
            }
          );
        });
    },
  });

  return {
    reconcile: mutation.mutateAsync,
    isReconciling: mutation.isLoading,
  };
};

export const useAccountingData = () => {
  const { vendors } = useVendors();
  const { accounts } = useAccounts();
  const { taxRates } = useTaxRates();
  const { customers } = useCustomers();
  const { items } = useItems();

  return {
    taxRates: taxRates || [],
    zeroTaxRate: (taxRates || []).find(
      (taxRate) =>
        taxRate.status === 'Active' && XERO_ZERO_TAX_IDS.includes(taxRate.id)
    ),
    vendors: vendors || [],
    customers: customers || [],
    items: items || [],
    accounts: accounts || [],
    supportedCurrencies: [],
    baseCurrency: '',
  };
};

export default function BillReconcilePage() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get('bill_id');
  const { data: bill, isLoading, error } = useBill(id);
  const { taxRates, vendors, accounts, supportedCurrencies, baseCurrency } =
    useAccountingData();

  if (isLoading) {
    return (
      <div>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-lg text-white">
        We could not find the bill you were looking for.
        <br />
        Please revisit the link that was sent to you.
      </div>
    );
  }

  return (
    bill && (
      <div className="mb-40">
        <div className="mx-auto mb-5 mt-10 max-w-7xl px-4 sm:px-6 md:px-8 "></div>
        <div className="mx-auto max-w-7xl px-4  sm:px-6 md:px-8">
          <div className="">
            <BillReconcileForm
              bill={bill}
              taxRates={taxRates}
              suppliers={vendors}
              accounts={accounts}
              supportedCurrencies={supportedCurrencies}
              baseCurrency={baseCurrency}
            />
          </div>
        </div>
      </div>
    )
  );
}
